import {assert} from 'Asserts';
import {
  isLeafTypeNumerical,
  LeafType,
  NumericalLeafTypes,
} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {BenchlingBlob} from 'schemas/tags/BlobConstants';
import {JSONValue, Override} from 'util/Types';

import {DataFrameGraphQL as RawDataFrameGraphQL} from './graphql/__generated/DataFrameGraphQL';

// Keep in sync with packages/benchling-dataframe-processing/src/benchling_dataframe_processing/columns/column_types/type_def_helpers.py
export enum TypeDefAliases {
  MAYBE_TEXT = 'MaybeText',
}

export interface DataFrameUnitInfo {
  unitApiId: string;
  unitVersionNumber: number;
}

export interface DataFrameColumnType {
  leafType: LeafType;
  isList?: true;
  isNullable?: true;
  maybeText?: true;
  schemaId?: string;
  unit?: DataFrameUnitInfo;
}

interface NumericalColumnType extends DataFrameColumnType {
  leafType: NumericalLeafTypes;
  isList: never;
  maybeText: never;
  schemaId: never;
}

type ColumnSystemName = string;

export interface DataFrameColumnDef {
  displayName: string;
  systemName: ColumnSystemName;
  type: DataFrameColumnType;
}

export type DataFrameRow = Record<ColumnSystemName, JSONValue>;

// This should be in sync with `HydrationStatus` in `hydrated_properties_types.py`
export enum HydrationStatus {
  HYDRATED = 'HYDRATED',
  UNHYDRATED = 'UNHYDRATED',
  INACCESSIBLE = 'INACCESSIBLE',
}

export interface UnhydratedObject {
  status: HydrationStatus.UNHYDRATED;
}

export interface InaccessibleObject {
  status: HydrationStatus.INACCESSIBLE;
}

export interface HydratedBlob {
  status: HydrationStatus.HYDRATED;
  blob: Pick<BenchlingBlob, 'id' | 'name' | 'url'>;
}

export interface HydratedAPIIdentifiedObject {
  status: HydrationStatus.HYDRATED;
  name: string;
}

export type BlobNode = HydratedBlob | InaccessibleObject | UnhydratedObject;

export type APIIdentifiedObjectNode = HydratedAPIIdentifiedObject | InaccessibleObject | UnhydratedObject;

export interface BlobDicts {
  [key: string]: BlobNode;
}

export interface APIIdentifiedObjects {
  [key: string]: APIIdentifiedObjectNode;
}

export interface HydratedProperties {
  apiIdentifiedObjects: APIIdentifiedObjects;
  blobDicts: BlobDicts;
}

export interface DataFrame {
  name: string;
  columns: Array<DataFrameColumnDef>;
  rows: Array<DataFrameRow>;
  hydratedProperties: HydratedProperties;
  // If not specified, can assume that rows represent all rows of the data frame, which is useful for
  // non-persisted data frames.
  numRows?: number;
  size?: number | null;
}

/**
 * @deprecated This type is generated based on `DeprecatedDataFrameFragment`. Use `PersistedDataFrameGraphQL` instead
 */
export type DataFrameGraphQL = Override<
  RawDataFrameGraphQL,
  {dataFrameJson: DataFrame; allColumnsJson: DataFrame['columns']}
>;

export type DataFrameSpec = Pick<DataFrame, 'name' | 'columns'>;
export type DataFrameColumns = DataFrame['columns'];

/**
 * Keep in sync with back-end DataType enum benchling/insights/statistics/data_type.py
 * @deprecated `BenchlingTypeDef.ts` defines the new type definitions used by `DataFrameColumnDef`.
 */
export enum DataType {
  BOOLEAN = 'BOOLEAN',
  DATE_OR_TIMESTAMP = 'DATE_OR_TIMESTAMP',
  JSON = 'JSON',
  NUMBER = 'NUMBER',
  STRING = 'STRING',
}

export interface DataTypeToTSType {
  [DataType.BOOLEAN]: boolean;
  [DataType.DATE_OR_TIMESTAMP]: string;
  [DataType.JSON]: JSONValue;
  [DataType.NUMBER]: number;
  [DataType.STRING]: string;
}

export type DataPointColumnsMappedType<DataPoint, NewType> = {
  [column in keyof DataPoint]: NewType;
};

// Helper mapped type to convert a point type like {x: unknown, y: unknown} and make it into a compatibility
// type like {x: DataType, y: DataType}, which is used to define the OutputColumnTypes generic for functions
// in this module.
export type DataPointColumnsToDataType<DataPoint> = DataPointColumnsMappedType<DataPoint, DataType>;
export type DataPointColumnsToDataTypes<DataPoint> = DataPointColumnsMappedType<DataPoint, Array<DataType>>;

// TODO(BNCH-24546): Update DeprecatedDataFrameColumnDef usages to new DataFrameColumnDef interface. In the mean
// time, any new work should use the DataFrameColumnDef interface.
export interface DeprecatedDataFrameColumnDef<TDataRow> {
  name: keyof TDataRow;
  type: DataType;
}

// TODO(BNCH-24546): Update DeprecatedDataFrame usages to new DataFrame interface. In the mean time, any new
// work should use the DataFrame interface.
export interface DeprecatedDataFrame<TDataRow> {
  axisTypes: DataPointColumnsToDataType<TDataRow>;
  points: Array<TDataRow>;
}

export interface IsMaskedDataRow {
  isMasked?: unknown;
}

export function isColumnTypeNumerical(columnType: DataFrameColumnType): columnType is NumericalColumnType {
  return (
    isLeafTypeNumerical(columnType.leafType) &&
    !columnType.isList &&
    !columnType.maybeText &&
    !columnType.schemaId
  );
}

export function assertColumnTypeNumerical(
  columnType: DataFrameColumnType
): asserts columnType is NumericalColumnType {
  assert(isColumnTypeNumerical(columnType), 'Expected column type to be numerical', {columnType});
}
